import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// No default 'Content-Type: application/json' header here (removed
// 2026-09-13) - axios already sets that on its own for a plain-object
// body via its default transformRequest, so it was redundant for JSON
// requests. For a FormData body (every file upload in this app - salary
// sheet import, task/issue import, KRA template import, AI document
// review, CRM/vendor card scans) it was actively harmful: axios only
// unsets a Content-Type it recognizes as its own auto-set default before
// letting the browser add the real multipart boundary - a Content-Type
// carried on the instance's own `headers` config reads as user-set, so it
// survives untouched, and the request interceptor below mutating
// `config.headers.Authorization` as a plain property (rather than through
// AxiosHeaders' own .set()) was re-triggering that path on every request,
// not just ones from this instance's initial defaults. The net effect:
// every multipart upload through this shared `api` instance went out as
// Content-Type: application/json, so FastAPI's multipart parser found no
// fields at all and rejected it with a 422 "file: Field required" -
// reproduced 2026-09-13 with a real browser FormData/File against the
// live backend (see the payroll salary-sheet import investigation this
// same day) and confirmed fixed by this removal alone, interceptor
// untouched.
const api = axios.create({
  baseURL: API_URL,
});

// Attach token automatically if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Basic 401 handling — bounce to login if token is invalid/expired.
// Also normalizes FastAPI's 422 validation "detail" (an array of {loc,msg,type}
// objects, not a string) into a plain string, since every page in this app
// does `error.response?.data?.detail || 'fallback'` and passes it straight to
// showToast() — rendering that array as-is crashes the toast component.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_id');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      error.response.data.detail = detail
        .map((d) => (typeof d === 'string' ? d : d.msg || JSON.stringify(d)))
        .join('; ');
    }

    return Promise.reject(error);
  }
);

export default api;
